/**
 * Rating History Chart Component
 *
 * Displays a chart showing score trajectory over 12 months with
 * status zone bands (In-Form/On-Track/Off-Track/Out-of-Form regions).
 *
 * The chart visualizes percentile rank history with colored background
 * bands indicating performance zones.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { FundRating, RankHistoryEntry } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Props for the RatingHistoryChart component
 */
export interface RatingHistoryChartProps {
  /** Rank history entries (monthly data points) */
  rankHistory: RankHistoryEntry[];
  /** Current rating for styling emphasis */
  currentRating?: FundRating;
  /** Optional title override */
  title?: string;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Chart dimensions and padding
 */
const CHART_CONFIG = {
  width: 400,
  height: 200,
  paddingTop: 20,
  paddingBottom: 30,
  paddingLeft: 40,
  paddingRight: 20,
} as const;

/**
 * Status zone configuration
 * Zones are defined as percentile ranges (0-100, where lower is better)
 */
const STATUS_ZONES: Array<{
  status: FundRating;
  label: string;
  minPercentile: number;
  maxPercentile: number;
  fillColor: string;
  strokeColor: string;
  textColor: string;
}> = [
  {
    status: "in-form",
    label: "In-Form",
    minPercentile: 0,
    maxPercentile: 25,
    fillColor: "rgba(16, 185, 129, 0.15)", // emerald-500 with opacity
    strokeColor: "#10b981",
    textColor: "text-emerald-600 dark:text-emerald-400",
  },
  {
    status: "on-track",
    label: "On-Track",
    minPercentile: 25,
    maxPercentile: 50,
    fillColor: "rgba(14, 165, 233, 0.15)", // sky-500 with opacity
    strokeColor: "#0ea5e9",
    textColor: "text-sky-600 dark:text-sky-400",
  },
  {
    status: "off-track",
    label: "Off-Track",
    minPercentile: 50,
    maxPercentile: 75,
    fillColor: "rgba(245, 158, 11, 0.15)", // amber-500 with opacity
    strokeColor: "#f59e0b",
    textColor: "text-amber-600 dark:text-amber-400",
  },
  {
    status: "out-of-form",
    label: "Out-of-Form",
    minPercentile: 75,
    maxPercentile: 100,
    fillColor: "rgba(244, 63, 94, 0.15)", // rose-500 with opacity
    strokeColor: "#f43f5e",
    textColor: "text-rose-600 dark:text-rose-400",
  },
];

/**
 * Get stroke color for a given rating
 */
function getStrokeColor(rating: FundRating): string {
  const zone = STATUS_ZONES.find((z) => z.status === rating);
  return zone?.strokeColor ?? "#6b7280";
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate percentile from rank (inverted so lower rank = lower percentile)
 */
function calculatePercentile(rank: number, totalFunds: number): number {
  if (totalFunds === 0) return 50;
  return (rank / totalFunds) * 100;
}

/**
 * Format month label from ISO date string
 */
function formatMonthLabel(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short" });
}

/**
 * Calculate chart inner dimensions
 */
function getChartDimensions() {
  const innerWidth =
    CHART_CONFIG.width - CHART_CONFIG.paddingLeft - CHART_CONFIG.paddingRight;
  const innerHeight =
    CHART_CONFIG.height - CHART_CONFIG.paddingTop - CHART_CONFIG.paddingBottom;
  return { innerWidth, innerHeight };
}

/**
 * Convert percentile to Y coordinate (inverted: lower percentile = higher on chart)
 */
function percentileToY(percentile: number): number {
  const { innerHeight } = getChartDimensions();
  // Clamp percentile to 0-100
  const clampedPercentile = Math.max(0, Math.min(100, percentile));
  // Lower percentile = better = higher on chart
  return CHART_CONFIG.paddingTop + (clampedPercentile / 100) * innerHeight;
}

/**
 * Convert data index to X coordinate
 */
function indexToX(index: number, totalPoints: number): number {
  const { innerWidth } = getChartDimensions();
  if (totalPoints <= 1) return CHART_CONFIG.paddingLeft + innerWidth / 2;
  return (
    CHART_CONFIG.paddingLeft + (index / (totalPoints - 1)) * innerWidth
  );
}

// ============================================================================
// Subcomponents
// ============================================================================

/**
 * Background zone bands showing performance regions
 */
function ZoneBands() {
  const { innerWidth } = getChartDimensions();

  return (
    <g className="zone-bands">
      {STATUS_ZONES.map((zone) => {
        const yStart = percentileToY(zone.minPercentile);
        const yEnd = percentileToY(zone.maxPercentile);
        const height = yEnd - yStart;

        return (
          <rect
            key={zone.status}
            x={CHART_CONFIG.paddingLeft}
            y={yStart}
            width={innerWidth}
            height={height}
            fill={zone.fillColor}
            className="transition-opacity duration-200"
          />
        );
      })}
    </g>
  );
}

/**
 * Y-axis with zone labels
 */
function YAxis() {
  const { innerHeight } = getChartDimensions();

  // Draw labels at zone boundaries
  const labelPositions = [
    { percentile: 12.5, label: "Top 25%" },
    { percentile: 37.5, label: "Top 50%" },
    { percentile: 62.5, label: "Top 75%" },
    { percentile: 87.5, label: "Bottom" },
  ];

  return (
    <g className="y-axis">
      {/* Axis line */}
      <line
        x1={CHART_CONFIG.paddingLeft}
        y1={CHART_CONFIG.paddingTop}
        x2={CHART_CONFIG.paddingLeft}
        y2={CHART_CONFIG.paddingTop + innerHeight}
        stroke="currentColor"
        strokeOpacity={0.2}
        strokeWidth={1}
      />
      {/* Zone boundary lines */}
      {[25, 50, 75].map((percentile) => (
        <line
          key={percentile}
          x1={CHART_CONFIG.paddingLeft}
          y1={percentileToY(percentile)}
          x2={CHART_CONFIG.width - CHART_CONFIG.paddingRight}
          y2={percentileToY(percentile)}
          stroke="currentColor"
          strokeOpacity={0.1}
          strokeWidth={1}
          strokeDasharray="4,4"
        />
      ))}
      {/* Labels */}
      {labelPositions.map(({ percentile, label }) => (
        <text
          key={percentile}
          x={CHART_CONFIG.paddingLeft - 4}
          y={percentileToY(percentile)}
          textAnchor="end"
          dominantBaseline="middle"
          className="fill-muted-foreground text-[9px]"
        >
          {label}
        </text>
      ))}
    </g>
  );
}

/**
 * X-axis with month labels
 */
function XAxis({ data }: { data: RankHistoryEntry[] }) {
  const { innerHeight } = getChartDimensions();

  // Show every 2nd or 3rd label to avoid crowding
  const labelInterval = data.length > 8 ? 2 : 1;

  return (
    <g className="x-axis">
      {/* Axis line */}
      <line
        x1={CHART_CONFIG.paddingLeft}
        y1={CHART_CONFIG.paddingTop + innerHeight}
        x2={CHART_CONFIG.width - CHART_CONFIG.paddingRight}
        y2={CHART_CONFIG.paddingTop + innerHeight}
        stroke="currentColor"
        strokeOpacity={0.2}
        strokeWidth={1}
      />
      {/* Month labels */}
      {data.map((entry, index) => {
        if (index % labelInterval !== 0 && index !== data.length - 1) {
          return null;
        }
        const x = indexToX(index, data.length);
        return (
          <text
            key={entry.date}
            x={x}
            y={CHART_CONFIG.paddingTop + innerHeight + 16}
            textAnchor="middle"
            className="fill-muted-foreground text-[10px]"
          >
            {formatMonthLabel(entry.date)}
          </text>
        );
      })}
    </g>
  );
}

/**
 * The main trajectory line with data points
 */
function TrajectoryLine({
  data,
  currentRating,
}: {
  data: RankHistoryEntry[];
  currentRating?: FundRating;
}) {
  if (data.length === 0) return null;

  // Calculate points
  const points = data.map((entry, index) => {
    const percentile = calculatePercentile(entry.rank, entry.totalFunds);
    return {
      x: indexToX(index, data.length),
      y: percentileToY(percentile),
      rating: entry.rating,
      percentile,
    };
  });

  // Create path
  const pathD = points
    .map((point, index) => {
      const command = index === 0 ? "M" : "L";
      return `${command} ${point.x},${point.y}`;
    })
    .join(" ");

  // Get the color based on current rating or last data point rating
  const lineColor = getStrokeColor(
    currentRating ?? data[data.length - 1]?.rating ?? "on-track"
  );

  return (
    <g className="trajectory-line">
      {/* Line shadow for depth */}
      <path
        d={pathD}
        fill="none"
        stroke={lineColor}
        strokeWidth={3}
        strokeOpacity={0.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Main trajectory line */}
      <path
        d={pathD}
        fill="none"
        stroke={lineColor}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="transition-all duration-300"
      />
      {/* Data points */}
      {points.map((point, index) => {
        const isLast = index === points.length - 1;
        const pointColor = getStrokeColor(data[index]?.rating ?? "on-track");

        return (
          <g key={data[index]?.date}>
            {/* Larger invisible hit area for tooltips */}
            <circle
              cx={point.x}
              cy={point.y}
              r={8}
              fill="transparent"
              className="cursor-pointer"
            />
            {/* Visible point */}
            <circle
              cx={point.x}
              cy={point.y}
              r={isLast ? 5 : 3}
              fill={pointColor}
              stroke="white"
              strokeWidth={isLast ? 2 : 1}
              className="transition-all duration-200"
            />
            {/* Highlight ring on last point */}
            {isLast && (
              <circle
                cx={point.x}
                cy={point.y}
                r={8}
                fill="none"
                stroke={pointColor}
                strokeWidth={2}
                strokeOpacity={0.3}
              />
            )}
          </g>
        );
      })}
    </g>
  );
}

/**
 * Legend showing zone colors and labels
 */
function ChartLegend() {
  return (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-4">
      {STATUS_ZONES.map((zone) => (
        <div key={zone.status} className="flex items-center gap-1.5">
          <div
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: zone.strokeColor }}
          />
          <span className={cn("text-xs font-medium", zone.textColor)}>
            {zone.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Empty state when no data is available
 */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
        <svg
          className="h-8 w-8 text-muted-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
          />
        </svg>
      </div>
      <p className="text-sm text-muted-foreground text-center">
        Rating history will appear here once enough data is available
      </p>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Rating History Chart
 *
 * Displays a 12-month trajectory of fund performance with colored zone bands
 * indicating rating categories (In-Form, On-Track, Off-Track, Out-of-Form).
 *
 * @example
 * ```tsx
 * <RatingHistoryChart
 *   rankHistory={[
 *     { date: "2024-01", rank: 5, totalFunds: 100, rating: "in-form" },
 *     { date: "2024-02", rank: 8, totalFunds: 100, rating: "in-form" },
 *     // ... more months
 *   ]}
 *   currentRating="in-form"
 * />
 * ```
 */
export function RatingHistoryChart({
  rankHistory,
  currentRating,
  title = "Rating History",
  className,
}: RatingHistoryChartProps) {
  // Take last 12 months of data
  const displayData = rankHistory.slice(-12);

  const hasData = displayData.length >= 2;

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <div className="w-full">
            <svg
              viewBox={`0 0 ${CHART_CONFIG.width} ${CHART_CONFIG.height}`}
              className="w-full h-auto"
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label={`Rating history chart showing ${displayData.length} months of performance data`}
            >
              <title>Rating History Chart</title>
              <desc>
                A line chart showing fund rank percentile over the last{" "}
                {displayData.length} months with colored zones indicating
                In-Form, On-Track, Off-Track, and Out-of-Form performance
                regions.
              </desc>
              {/* Background zone bands */}
              <ZoneBands />
              {/* Axes */}
              <YAxis />
              <XAxis data={displayData} />
              {/* Main trajectory line */}
              <TrajectoryLine
                data={displayData}
                currentRating={currentRating}
              />
            </svg>
            {/* Legend */}
            <ChartLegend />
          </div>
        ) : (
          <EmptyState />
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for RatingHistoryChart
 */
export function RatingHistoryChartSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <Card className={cn(className)}>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Chart area skeleton */}
          <Skeleton className="w-full h-48 rounded-lg" />
          {/* Legend skeleton */}
          <div className="flex justify-center gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <Skeleton className="h-2.5 w-2.5 rounded-sm" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default RatingHistoryChart;
